/**
 * IDLE LIFE (B4, spec §E.5): when light packets may travel, and the loop that sends them.
 *
 * Paused while the tab is hidden, under reduced motion, during the intro or a drive, during a gesture and
 * for 1200 ms after the last pointer or wheel input, and after 90 s without any input (resumes on input).
 * A packet already travelling finishes; the next one waits.
 */
import { isAbort, wait } from "@/lib/ticker";

export const GESTURE_GRACE_MS = 1200;
export const IDLE_SLEEP_MS = 90_000;
const POLL_MS = 250;

export interface IdleInputs {
  now: number;
  hidden: boolean;
  reduced: boolean;
  /** The intro or a drive is running. */
  blocked: boolean;
  /** Pointers currently down (a gesture in progress). */
  pointersDown: number;
  /** Last pointer or wheel input (ms), -Infinity if none. */
  lastPointerAt: number;
  /** Last input of any kind (pointer, wheel, key, move), -Infinity if none. */
  lastInputAt: number;
  /** When the tracker started (a fresh page counts as "input" for the 90 s rule). */
  since: number;
}

export function idlePaused(i: IdleInputs): boolean {
  if (i.hidden || i.reduced || i.blocked) return true;
  if (i.pointersDown > 0) return true;
  if (i.now - i.lastPointerAt < GESTURE_GRACE_MS) return true;
  const lastAny = Math.max(i.lastInputAt, i.since);
  return i.now - lastAny >= IDLE_SLEEP_MS;
}

/** Window-level input tracker (passive, capture). One per choreographer; stop() removes every listener. */
export class InputTracker {
  pointersDown = 0;
  lastPointerAt = -Infinity;
  lastInputAt = -Infinity;
  readonly since: number;
  private readonly down = new Set<number>();
  private readonly offs: Array<() => void> = [];

  constructor(target: (Window & typeof globalThis) | null = typeof window !== "undefined" ? window : null) {
    this.since = typeof performance !== "undefined" ? performance.now() : 0;
    if (!target) return;
    const opts = { capture: true, passive: true } as const;
    const listen = <K extends keyof WindowEventMap>(type: K, fn: (e: WindowEventMap[K]) => void) => {
      target.addEventListener(type, fn, opts);
      this.offs.push(() => target.removeEventListener(type, fn, opts));
    };
    const stamp = (pointer: boolean) => {
      const t = performance.now();
      this.lastInputAt = t;
      if (pointer) this.lastPointerAt = t;
    };
    listen("pointerdown", (e) => {
      this.down.add(e.pointerId);
      this.pointersDown = this.down.size;
      stamp(true);
    });
    const up = (e: PointerEvent) => {
      this.down.delete(e.pointerId);
      this.pointersDown = this.down.size;
      stamp(true);
    };
    listen("pointerup", up);
    listen("pointercancel", up);
    listen("pointermove", () => stamp(this.pointersDown > 0));
    listen("wheel", () => stamp(true));
    listen("keydown", () => stamp(false));
  }

  stop(): void {
    for (const off of this.offs.splice(0)) off();
    this.down.clear();
    this.pointersDown = 0;
  }
}

const hiddenNow = (): boolean => typeof document !== "undefined" && document.visibilityState === "hidden";

export interface LoopOptions {
  signal: AbortSignal;
  /** ms before the first packet. */
  first: number;
  /** ms between the end of one packet and the next. */
  interval: () => number;
  /** Extra pause conditions (reduced motion, intro, drive) on top of the input rules. */
  blocked: () => boolean;
  reduced: () => boolean;
  tracker: InputTracker;
  packet: (signal: AbortSignal) => Promise<unknown>;
}

/** Runs packets until the signal aborts. Never rejects. */
export async function runIdleLoop(o: LoopOptions): Promise<void> {
  const paused = () =>
    idlePaused({
      now: performance.now(),
      hidden: hiddenNow(),
      reduced: o.reduced(),
      blocked: o.blocked(),
      pointersDown: o.tracker.pointersDown,
      lastPointerAt: o.tracker.lastPointerAt,
      lastInputAt: o.tracker.lastInputAt,
      since: o.tracker.since,
    });
  try {
    await wait(Math.max(0, o.first), o.signal);
    for (;;) {
      while (paused()) await wait(POLL_MS, o.signal);
      await o.packet(o.signal);
      if (o.signal.aborted) return;
      await wait(Math.max(0, o.interval()), o.signal);
    }
  } catch (e) {
    if (!isAbort(e)) console.error("[choreo] idle loop", e);
  }
}
